import React from 'react';
import {
  TextField, Typography, Box, Grid, Button, Paper, Snackbar, Alert, Chip, Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconPiesJuntos from '../../../images/pies_juntos.png';
import IconSemitandem from '../../../images/semitandem.png';
import IconTandem from '../../../images/tandem.png';
import { useCapacidadFuncionalForm } from '../hooks/useCapacidadFuncionalForm';

const C = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: C.primary },
    '&.Mui-focused fieldset': { borderColor: C.primary, borderWidth: 2 },
  },
};

const disabledFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f1f5f9',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#e2e8f0' },
  },
};

const sectionSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${C.border}`,
  background: '#f8fbff',
  boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
};

// ── Puntos chip ────────────────────────────────────────────────────────────
const PtsChip = ({ pts, max, hasData }) => (
  <Chip
    label={hasData ? `${pts} / ${max} pt${max !== 1 ? 's' : ''}` : `— / ${max} pt${max !== 1 ? 's' : ''}`}
    size="small"
    sx={{
      fontWeight: 700,
      minWidth: 68,
      backgroundColor: !hasData ? '#f1f5f9' : pts === 0 ? '#fee2e2' : pts === max ? '#dcfce7' : '#dbeafe',
      color: !hasData ? '#94a3b8' : pts === 0 ? '#991b1b' : pts === max ? '#166534' : '#1e3a8a',
    }}
  />
);

// ── Criterios en texto ──────────────────────────────────────────────────────
const Criterios = ({ items }) => (
  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1, lineHeight: 1.6 }}>
    {items.join('  ·  ')}
  </Typography>
);

// ── Fila de test de equilibrio ─────────────────────────────────────────────
const EquilibrioRow = ({ icon, alt, criterios, label, name, value, onChange, pts, maxPts }) => {
  const hasData = value !== '' && value !== null;
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, py: 1.5,
        borderBottom: `1px solid ${C.border}`,
        '&:last-child': { borderBottom: 'none', pb: 0 },
      }}
    >
      {/* Icono */}
      <Box sx={{ flexShrink: 0, width: 52, display: 'flex', justifyContent: 'center' }}>
        <img src={icon} alt={alt} style={{ height: 52, width: 52, objectFit: 'contain', display: 'block' }} />
      </Box>

      {/* Campo */}
      <Box sx={{ flex: 1 }}>
        <Criterios items={criterios} />
        <TextField
          fullWidth size="small"
          label={label} variant="outlined"
          name={name} value={value}
          onChange={onChange}
          inputProps={{ inputMode: 'decimal' }}
          sx={fieldSx}
        />
      </Box>

      {/* Chip */}
      <Box sx={{ flexShrink: 0 }}>
        <PtsChip pts={pts} max={maxPts} hasData={hasData} />
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────

const CapacidadFuncional = ({ idPaciente }) => {
  const {
    formData,
    velocidadMarcha,
    sppbTotal,
    ptPiesJuntos,
    ptSemitandem,
    ptTandem,
    ptEquilibrio,
    ptDesplazamiento,
    ptFuerza,
    esFragilTUG,
    openGood,
    openError,
    setOpenGood,
    setOpenError,
    handleChange,
    guardarDatos,
  } = useCapacidadFuncionalForm({ idPaciente });

  // Solo se califica como frágil si hay algún dato ingresado
  const hayDatos = [
    formData.tiempoPiesJuntos,
    formData.tiempoSemitandem,
    formData.tiempoTandem,
    formData.tiempoDesplazamiento,
    formData.tiempoSentadillas,
  ].some((v) => v !== '' && v !== null);

  const esFragil = hayDatos && (sppbTotal < 10 || esFragilTUG);

  // Color del panel de puntuación total
  const scoreStyle = !hayDatos
    ? { border: '#94a3b8', bg: '#f8fafc', text: '#64748b' }
    : sppbTotal >= 10
    ? { border: '#166534', bg: '#dcfce7', text: '#166534' }
    : sppbTotal >= 7
    ? { border: '#d97706', bg: '#fef3c7', text: '#92400e' }
    : { border: '#dc2626', bg: '#fee2e2', text: '#991b1b' };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Paper
        elevation={0}
        sx={{ width: '100%', p: { xs: 2, md: 3 }, borderRadius: 4, border: `1px solid ${C.border}` }}
      >
        <Typography variant="h4" sx={{ textAlign: 'center', color: C.textMain, fontWeight: 700, mb: 0.5 }}>
          Capacidad Funcional — SPPB
        </Typography>
        <Typography sx={{ textAlign: 'center', color: C.textMuted, mb: 3, fontSize: '0.92rem' }}>
          Short Physical Performance Battery · Evaluación del equilibrio, marcha y fuerza.
        </Typography>

        {/* ── Panel puntuación total ──────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            mb: 3, p: { xs: 2, md: 2.5 }, borderRadius: 3,
            border: `2px solid ${scoreStyle.border}`,
            background: scoreStyle.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.2, color: scoreStyle.text }}>
              Total SPPB
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1, color: scoreStyle.text }}>
                {hayDatos ? sppbTotal : '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: scoreStyle.text }}> / 12</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
            {[
              { label: 'Equilibrio', pts: ptEquilibrio, max: 4 },
              { label: 'Marcha', pts: ptDesplazamiento, max: 4 },
              { label: 'Fuerza', pts: ptFuerza, max: 4 },
            ].map(({ label, pts, max }) => (
              <Chip
                key={label}
                size="small"
                label={`${label}: ${hayDatos ? pts : '—'}/${max}`}
                variant="outlined"
                sx={{ fontWeight: 600, borderColor: scoreStyle.border, color: scoreStyle.text }}
              />
            ))}
            {hayDatos && (
              <Chip
                label={esFragil ? 'Frágil' : 'No frágil'}
                sx={{
                  fontWeight: 700,
                  backgroundColor: esFragil ? '#dc2626' : '#16a34a',
                  color: '#fff',
                }}
              />
            )}
          </Box>
        </Paper>

        {/* ── Equilibrio ─────────────────────────────────────────────────── */}
        <Paper elevation={0} sx={sectionSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: C.textMain, fontWeight: 700 }}>
              Prueba de Equilibrio Estático
            </Typography>
            <PtsChip pts={ptEquilibrio} max={4} hasData={
              formData.tiempoPiesJuntos !== '' || formData.tiempoSemitandem !== '' || formData.tiempoTandem !== ''
            } />
          </Box>

          <EquilibrioRow
            icon={IconPiesJuntos} alt="Pies juntos"
            criterios={['< 10" → 0 pts', '≥ 10" → 1 pt']}
            label="Pies juntos (s)" name="tiempoPiesJuntos"
            value={formData.tiempoPiesJuntos} onChange={handleChange}
            pts={ptPiesJuntos} maxPts={1}
          />
          <EquilibrioRow
            icon={IconSemitandem} alt="Semitándem"
            criterios={['< 10" → 0 pts', '≥ 10" → 1 pt']}
            label="Semitándem (s)" name="tiempoSemitandem"
            value={formData.tiempoSemitandem} onChange={handleChange}
            pts={ptSemitandem} maxPts={1}
          />
          <EquilibrioRow
            icon={IconTandem} alt="Tándem"
            criterios={['< 3" → 0 pts', '3"–9.99" → 1 pt', '≥ 10" → 2 pts']}
            label="Tándem (s)" name="tiempoTandem"
            value={formData.tiempoTandem} onChange={handleChange}
            pts={ptTandem} maxPts={2}
          />
        </Paper>

        {/* ── Desplazamiento 4 m ─────────────────────────────────────────── */}
        <Paper elevation={0} sx={sectionSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box>
              <Typography variant="h6" sx={{ color: C.textMain, fontWeight: 700 }}>
                Prueba de Desplazamiento 4 m
              </Typography>
              <Typography variant="caption" sx={{ color: C.textMuted }}>
                Dos tomas de tiempo, se toma la menor · Primera puntera, último talón.
              </Typography>
            </Box>
            <PtsChip pts={ptDesplazamiento} max={4} hasData={formData.tiempoDesplazamiento !== ''} />
          </Box>
          <Divider sx={{ my: 1.5, borderColor: C.border }} />

          <Criterios items={['No camina → 0', '> 8.7" → 1 pt', '6.21"–8.7" → 2 pts', '4.82"–6.20" → 3 pts', '< 4.82" → 4 pts']} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small"
                label="Tiempo (s)" variant="outlined"
                name="tiempoDesplazamiento" value={formData.tiempoDesplazamiento}
                onChange={handleChange} inputProps={{ inputMode: 'decimal' }} sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small"
                label="Velocidad de marcha (m/s)"
                value={velocidadMarcha} disabled sx={disabledFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <PtsChip pts={ptDesplazamiento} max={4} hasData={formData.tiempoDesplazamiento !== ''} />
            </Grid>
          </Grid>
        </Paper>

        {/* ── 5 Sentadillas — desde Encoder ──────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{ ...sectionSx, background: '#fafafa', border: `1px dashed #cbd5e1` }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box>
              <Typography variant="h6" sx={{ color: C.textMain, fontWeight: 700 }}>
                Prueba de Fuerza — 5 Sentadillas
              </Typography>
              <Typography variant="caption" sx={{ color: C.textMuted }}>
                Brazos cruzados sobre el pecho · Inicio sentado, finaliza en 5ª bipedestación.
              </Typography>
            </Box>
            <PtsChip pts={ptFuerza} max={4} hasData={formData.tiempoSentadillas !== ''} />
          </Box>
          <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{ mb: 2, borderRadius: 2, py: 0.5, fontSize: '0.82rem' }}
          >
            Esta prueba se realiza durante la sesión con el <strong>Encoder (5STS)</strong>.
            El tiempo se sincroniza automáticamente al guardar el Encoder.
          </Alert>

          <Criterios items={['No puede / ≥ 60" → 0', '16.70"–59.99" → 1 pt', '13.70"–16.69" → 2 pts', '11.20"–13.69" → 3 pts', '< 11.20" → 4 pts']} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small"
                label="Tiempo 5STS (s) — desde Encoder"
                value={formData.tiempoSentadillas}
                disabled sx={disabledFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <PtsChip pts={ptFuerza} max={4} hasData={formData.tiempoSentadillas !== ''} />
            </Grid>
          </Grid>
        </Paper>

        {/* ── TUG ────────────────────────────────────────────────────────── */}
        <Paper elevation={0} sx={sectionSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box>
              <Typography variant="h6" sx={{ color: C.textMain, fontWeight: 700 }}>
                Timed Up &amp; Go (TUG)
              </Typography>
              <Typography variant="caption" sx={{ color: C.textMuted }}>
                Levantarse de la silla, caminar 3 m y volver a sentarse.
              </Typography>
            </Box>
            {formData.tiempoTUG && (
              <Chip
                label={esFragilTUG ? 'Frágil ≥ 12 s' : 'Normal < 12 s'}
                sx={{
                  fontWeight: 700,
                  backgroundColor: esFragilTUG ? '#fee2e2' : '#dcfce7',
                  color: esFragilTUG ? '#991b1b' : '#166534',
                }}
              />
            )}
          </Box>
          <Divider sx={{ my: 1.5, borderColor: C.border }} />
          <Criterios items={['Normal: < 12 s', 'Frágil: ≥ 12 s']} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small"
                label="Tiempo (s)" variant="outlined"
                name="tiempoTUG" value={formData.tiempoTUG}
                onChange={handleChange} inputProps={{ inputMode: 'decimal' }} sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Resumen fragilidad ─────────────────────────────────────────── */}
        <Paper elevation={0} sx={{ ...sectionSx, background: '#fff', mb: 3 }}>
          <Typography variant="h6" sx={{ color: C.textMain, fontWeight: 700, mb: 2 }}>
            Resumen Fragilidad
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'SPPB', value: hayDatos ? `${sppbTotal} / 12` : '—', unit: 'pts' },
              { label: 'VELOCIDAD', value: velocidadMarcha || '—', unit: 'm/s' },
              { label: 'TUG', value: formData.tiempoTUG || '—', unit: 's' },
              { label: '5STS', value: formData.tiempoSentadillas || '—', unit: 's' },
            ].map(({ label, value, unit }) => (
              <Grid item xs={6} sm={3} key={label}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textMain, lineHeight: 1.3 }}>
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ color: C.textMuted }}>{unit}</Typography>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {hayDatos ? (
              <Chip
                label={!esFragil ? 'No frágil — SPPB ≥ 10 y TUG < 12 s' : 'Frágil — SPPB < 10 o TUG ≥ 12 s'}
                sx={{
                  fontWeight: 600,
                  backgroundColor: !esFragil ? '#dcfce7' : '#fee2e2',
                  color: !esFragil ? '#166534' : '#991b1b',
                }}
              />
            ) : (
              <Chip label="Pendiente de datos" sx={{ fontWeight: 600, backgroundColor: '#f1f5f9', color: '#64748b' }} />
            )}
            {formData.tiempoSentadillas && parseFloat(formData.tiempoSentadillas) >= 11.64 && (
              <Chip
                label="Pérdida de fuerza muscular (≥ 11.64 s)"
                sx={{ fontWeight: 600, backgroundColor: '#fef3c7', color: '#92400e' }}
              />
            )}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 5 }}>
            Guardar datos
          </Button>
        </Box>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>¡Datos guardados correctamente!</Alert>
        </Snackbar>
        <Snackbar open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
          <Alert severity="error" onClose={() => setOpenError(false)}>Error al guardar los datos.</Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default CapacidadFuncional;
